import { SeamTapeGrain } from '@seddi/scene';
import { BackSide, Matrix3, Mesh, type MeshPhysicalMaterial, type Object3D } from '@seddi/three';

import { assert } from '../core/assert';
import { updateRepeatTexturesTransform } from '../materials/fabricMaterial';
import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import { ResourcesContext } from '../resources/resourcesContext';
import {
  attachFabric as attachFabricBase,
  detachFabric,
  detachGarmentOcclusion,
  geometryPlaceholder,
  materialPlaceholder0,
  materialPlaceholder1,
  type VirtualPieceRenderable,
} from './virtualPieceRenderable';

export interface SeamTapeRenderable extends VirtualPieceRenderable {}

/**
 * Create seam tape renderable.
 * @param id - Seam ID.
 * @param parent - Seam object.
 */
export const create = (id: string, parent: Object3D): SeamTapeRenderable => {
  // Create new mesh object with placeholder geometry and materials.
  const materials = [materialPlaceholder1.clone(), materialPlaceholder0.clone()];
  const mesh = new Mesh(geometryPlaceholder, materials);
  mesh.name = `${id}-tape`;
  // Hierarchy remains immutable.
  parent.add(mesh);
  // Return new instance.
  return { id, mesh, parent };
};

/**
 * Dispose seam tape renderable.
 * @param self - Seam tape renderable.
 * @param resources - Resources manager.
 */
export const dispose = (self: SeamTapeRenderable, resources: ResourcesContext) => {
  // Detach resources.
  detachGarmentGeometry(self, resources);
  detachGarmentOcclusion(self, resources);
  detachFabric(self, resources);
  // Remove from parent.
  self.parent.remove(self.mesh);
};

// ------ FABRIC ------

/**
 * Attach fabric.
 * @param self - Seam tape renderable.
 * @param id - Fabric ID.
 * @param resources - Resources context;
 */
export const attachFabric = (
  self: SeamTapeRenderable,
  id: string,
  grain: SeamTapeGrain,
  resources: ResourcesContext,
) => {
  attachFabricBase(self, id, resources);

  updateGrain(self, grain);
};

// ------ GARMENT GEOMETRY ------

/**
 * Attach garment geometry.
 * @param self - Seam tape renderable.
 * @param garmentGeometry - Garment geometry.
 * @param resources - Resources context.
 */
export const attachGarmentGeometry = (
  self: SeamTapeRenderable,
  garmentGeometry: GarmentGeometryGfx,
  resources: ResourcesContext,
) => {
  if (self.garmentGeometryRef) {
    detachGarmentGeometry(self, resources);
  }
  // const garmentGeometry = resources.getRefGarmentGeometry(garmentGeometryId);
  // self.garmentGeometryRef = garmentGeometryId;
  const geometry = garmentGeometry.virtualPieces.get(self.id)!;
  assert(geometry, `Virtual piece ${self.id} not found`);
  self.mesh.geometry = geometry;
  // Recreate sub-meshes.
  recreateGroups(self);
};

/**
 * Detach garment geometry.
 * @param self - Seam tape renderable.
 * @param resources - Resources context;
 */
export const detachGarmentGeometry = (self: SeamTapeRenderable, resources: ResourcesContext) => {
  // if (!self.garmentGeometryRef) {
  //   return;
  // }
  self.mesh.geometry = geometryPlaceholder;
  // Recreate sub-meshes.
  recreateGroups(self);
  // resources.retRefGarmentGeometry(self.garmentGeometryRef);
  // delete self.garmentGeometryRef;
};

/**
 * Update grain.
 * @param self - Seam tape renderable.
 * @param grain - Grain option.
 */
export const updateGrain = (() => {
  const M = new Matrix3();
  const R_1 = new Matrix3();

  return (self: SeamTapeRenderable, grain: SeamTapeGrain) => {
    let rotation = 0;
    switch (grain) {
      case SeamTapeGrain.BIAS:
        rotation = Math.PI / 4;
        break;
      case SeamTapeGrain.CROSS:
        rotation = Math.PI / 2;
        break;
    }

    const mesh = self.mesh;
    const materials = Array.isArray(mesh.material)
      ? (mesh.material as MeshPhysicalMaterial[])
      : [mesh.material as MeshPhysicalMaterial];

    // Apply inverse grain rotation.
    R_1.makeRotation(-rotation);

    // Update every texture key found on each material.
    for (const material of materials) {
      if (!material.userData?.fabric) {
        continue;
      }

      // M = S x R-1
      const { repeat } = material.map;
      // For back-side textures apply horizontal flip.
      const sx = material.side === BackSide ? -1 : 1;
      M.makeScale(sx * repeat.x, repeat.y);
      M.multiply(R_1);

      updateRepeatTexturesTransform(material, M);
    }
  };
})();

/**
 * Recreate geometry groups.
 * @param self - Seam tape renderable.
 */
const recreateGroups = (self: SeamTapeRenderable) => {
  const mesh = self.mesh;
  const geometry = mesh.geometry;
  // Group geometry several times with different materials.
  const start = 0;
  const count = geometry.index?.count ?? 0;
  geometry.clearGroups();
  for (let i = 0; i < 2; ++i) {
    geometry.addGroup(start, count, i);
  }
};
